import { useState, type FormEvent } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AuthLayout } from "~/components/auth-layout";
import { Field, fieldMessageId } from "~/components/field";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { api, APIError } from "~/lib/api";
import { hasName, isOps } from "~/lib/auth";

export const Route = createFileRoute("/_authed/onboarding")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData({ queryKey: ["me"], queryFn: () => api.me() });
    if (isOps(me)) throw redirect({ to: "/ops" });
    if (hasName(me)) throw redirect({ to: "/" });
  },
  component: Onboarding,
});

function Onboarding() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const fullName = name.trim().replace(/\s+/g, " ");
    if (fullName.length < 2) {
      setError("Enter your name so we know who your parcels belong to.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      qc.setQueryData(["me"], await api.updateProfile({ name: fullName }));
      await navigate({ to: "/" });
    } catch (err) {
      setError(err instanceof APIError ? "We could not save your name. Try again." : "No connection. Check your internet and try again.");
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="What's your name?"
      description="Your number is verified. Tell us your name so our team knows who each parcel belongs to."
    >
      <form className="space-y-6" onSubmit={submit} noValidate>
        <Field id="name" label="Full name" error={error}>
          <Input
            id="name"
            name="name"
            density="comfortable"
            autoFocus
            autoComplete="name"
            autoCapitalize="words"
            enterKeyHint="done"
            placeholder="Amina Wanjiru"
            value={name}
            aria-invalid={!!error || undefined}
            aria-describedby={error ? fieldMessageId("name") : undefined}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
          />
        </Field>
        <Button type="submit" size="touch" className="w-full" loading={busy}>
          Continue
        </Button>
      </form>
    </AuthLayout>
  );
}
